import { useEffect, useState, useMemo } from "react";
import Sidebar from "../components/Sidebar";
import { database } from "../constants/firebase";
import { ref, onValue, off } from "firebase/database";
import { useUser } from "../contexts/UserContext";
import MobileHeader from "../components/MobileHeader";
import { LuUsers, LuActivity, LuTrendingUp, LuChevronLeft, LuChevronRight, LuDollarSign, LuShoppingBag, LuCalendar } from "react-icons/lu";
import useMoveTo from "../constants/navigation";
import { ICONS } from "../constants/icons";

const AdminAnalytics = () => {
    const { currentUser } = useUser();
    const navigate = useMoveTo();
    
    // Stats & Data state
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalSubscribers: 0,
        loginsToday: 0,
        grossRevenue: 0,
        marketplaceRevenue: 0,
        subscriptionRevenue: 0,
        vouchersCount: 0,
        loading: true,
    });
    
    const [allUsers, setAllUsers] = useState([]);
    const [allTransactions, setAllTransactions] = useState([]);
    
    // Interaction state
    const [activeFilter, setActiveFilter] = useState("all"); 
    const [timeFilter, setTimeFilter] = useState("all_time"); // "today", "7d", "30d", "all_time"
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 10;

    useEffect(() => {
        if (!currentUser?.isAdmin) return;

        console.log("💎 ADMIN ANALYTICS INITIALIZING...");
        const todayKey = new Date().toISOString().split("T")[0];

        // 1. Listen to Transactions
        const txRef = ref(database, "Transactions");
        const unsubTxs = onValue(txRef, (snapshot) => {
            if (!snapshot.exists()) {
                setStats(prev => ({ ...prev, marketplaceRevenue: 0 }));
                setAllTransactions([]);
                return;
            }

            const data = snapshot.val();
            let totalMarketRevenue = 0;
            const txList = [];

            Object.entries(data).forEach(([uid, userTxs]) => {
                if (typeof userTxs === 'object' && userTxs !== null) {
                    Object.entries(userTxs).forEach(([txId, txData]) => {
                        const amount = parseFloat(txData.totalPrice || txData.amount || txData.basePrice || 0);
                        totalMarketRevenue += amount;
                        txList.push({
                            id: txId,
                            userId: uid,
                            ...txData,
                            amount: amount
                        });
                    });
                }
            });

            setStats(prev => ({ ...prev, marketplaceRevenue: totalMarketRevenue }));
            setAllTransactions(txList.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0)));
        }, (err) => console.error("❌ Transactions Error:", err));

        // 2. Listen to Users
        const usersRef = ref(database, "Users");
        const unsubUsers = onValue(usersRef, (snapshot) => {
            if (!snapshot.exists()) {
                setStats(prev => ({ ...prev, totalUsers: 0, loading: false }));
                return;
            }

            const users = snapshot.val();
            let totalUsers = 0;
            let totalSubscribers = 0;
            let loginsToday = 0;
            let estimatedSubRevenue = 0;
            let vouchersCount = 0;
            const usersList = [];

            Object.entries(users).forEach(([uid, data]) => {
                totalUsers++;
                const isSubscriber = data.role === "Host" || data.subscriptionStatus === "active" || data.subscriptionStatus === "lifetime";
                const isPaidSubscriber = data.subscriptionStatus === "active" || data.subscriptionStatus === "canceling";
                const isLoggedToday = data.activityLog && data.activityLog[todayKey];

                if (isSubscriber) totalSubscribers++;
                if (isLoggedToday) loginsToday++;
                if (data.subscriptionStatus === "lifetime" || data.voucherRedeemed) vouchersCount++;
                if (isPaidSubscriber) {
                    const price = data.subscriptionType === "Yearly" ? 129.99 : 12.99;
                    estimatedSubRevenue += price;
                }

                usersList.push({ uid, ...data, isSubscriber, isLoggedToday });
            });

            setStats(prev => ({
                ...prev,
                totalUsers,
                totalSubscribers,
                loginsToday,
                subscriptionRevenue: estimatedSubRevenue,
                vouchersCount,
                loading: false,
            }));
            setAllUsers(usersList.sort((a, b) => (b.lastSeen || 0) - (a.lastSeen || 0)));
        }, (err) => console.error("❌ Users Error:", err));

        return () => {
            off(txRef);
            off(usersRef);
        };
    }, [currentUser]);

    // 🕒 Helper: Check if date is within range
    const isDateInRange = (dateStr) => {
        if (timeFilter === "all_time") return true;
        if (!dateStr) return false;
        
        const date = new Date(dateStr);
        const now = new Date();
        const diffDays = (now - date) / (1000 * 60 * 60 * 24);

        if (timeFilter === "today") return diffDays < 1;
        if (timeFilter === "7d") return diffDays < 7;
        if (timeFilter === "30d") return diffDays < 30;
        return true;
    };

    // 📊 Filtered & Time-Adjusted Stats
    const filteredStats = useMemo(() => {
        const filteredTxs = allTransactions.filter(tx => isDateInRange(tx.createdAt || tx.timestamp));
        const marketRev = filteredTxs.reduce((sum, tx) => sum + (tx.amount || 0), 0);
        
        // Note: Subscription revenue is harder to "time filter" perfectly since it's monthly,
        // but we'll show current active subs as the baseline for now.
        return {
            marketplaceRevenue: marketRev,
            grossRevenue: marketRev + stats.subscriptionRevenue,
            transactionCount: filteredTxs.length
        };
    }, [allTransactions, timeFilter, stats.subscriptionRevenue]);

    const filteredData = useMemo(() => {
        switch (activeFilter) {
            case "subscribers": return allUsers.filter(u => u.isSubscriber);
            case "logins": return allUsers.filter(u => u.isLoggedToday);
            case "transactions": return allTransactions.filter(tx => isDateInRange(tx.createdAt || tx.timestamp));
            default: return allUsers;
        }
    }, [activeFilter, allUsers, allTransactions, timeFilter]);

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const paginatedData = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredData.slice(start, start + itemsPerPage);
    }, [filteredData, currentPage]);

    useEffect(() => { setCurrentPage(1); }, [activeFilter, timeFilter]);

    const StatCard = ({ id, title, value, icon: Icon, color, subValue }) => {
        const isActive = activeFilter === id;
        return (
            <button 
                onClick={() => setActiveFilter(id)}
                className={`flex-1 text-left p-6 rounded-2xl border transition-all duration-300 flex items-center space-x-4 outline-none ${
                    isActive 
                    ? "bg-white dark:bg-[#252525] border-[#327691] shadow-lg scale-[1.02] ring-2 ring-[#327691]/20" 
                    : "bg-white dark:bg-[#1f1f1f] border-gray-100 dark:border-gray-800 shadow-sm hover:border-gray-300 dark:hover:border-gray-600 opacity-80"
                }`}
            >
                <div className={`p-3 rounded-xl ${color} bg-opacity-10 text-xl`}>
                    <Icon className={color.replace('bg-', 'text-')} />
                </div>
                <div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">{title}</p>
                    <h3 className="text-2xl font-bold dark:text-white">
                        {stats.loading ? "..." : (id === "transactions" ? `$${value.toLocaleString(undefined, {minimumFractionDigits: 2})}` : value)}
                    </h3>
                    {subValue && (
                        <p className="text-[10px] text-gray-400 mt-1 uppercase tracking-wider">{subValue}</p>
                    )}
                </div>
            </button>
        );
    };

    return (
        <div className="h-screen flex flex-col bg-white dark:bg-[#1A1A1A] text-black dark:text-white">
            <MobileHeader title={"Admin Analytics"} isBackBtn={true} />
            <div className="flex flex-1 overflow-hidden">
                <Sidebar />
                <main className="flex-1 min-h-0 overflow-y-auto pt-5 pb-24 md:pb-20 px-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between pb-6 border-b border-gray-100 dark:border-gray-800 gap-4">
                        <div className="flex items-center space-x-4">
                            <button onClick={() => navigate(-1)} className="hidden md:block">
                                <img src={ICONS.arrowBack} className="w-5 h-5 grayscale brightness-0 dark:invert" alt="Back" />
                            </button>
                            <h2 className="text-2xl md:text-3xl font-bold">Analytics Dashboard</h2>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-8">
                        <StatCard id="all" title="Total Users" value={stats.totalUsers} icon={LuUsers} color="bg-blue-500" />
                        <StatCard id="subscribers" title="Hosts" value={stats.totalSubscribers} icon={LuTrendingUp} color="bg-emerald-500" subValue={`Vouchers: ${stats.vouchersCount}`} />
                        <StatCard id="logins" title="Logins Today" value={stats.loginsToday} icon={LuActivity} color="bg-orange-500" />
                        <StatCard id="transactions" title="Gross Revenue" value={filteredStats.grossRevenue} icon={LuDollarSign} color="bg-purple-500" 
                            subValue={`Market: $${filteredStats.marketplaceRevenue.toFixed(0)} | Subs: $${stats.subscriptionRevenue.toFixed(0)}`} />
                    </div>

                    <div className="mt-10 mb-10">
                        <div className="flex items-center justify-between mb-6">
                            <h3 className="text-xl font-bold flex items-center">
                                {activeFilter === "transactions" ? "Transaction Ledger" : "User Directory"}
                                <span className="ml-2 text-sm font-normal text-gray-500">({filteredData.length})</span>
                                {activeFilter === "transactions" && timeFilter !== "all_time" && (
                                    <span className="ml-3 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 text-[10px] rounded uppercase tracking-widest font-bold">
                                        Filtered
                                    </span>
                                )}
                            </h3>

                            {/* 🕒 Time Filter Selector (Only for Transactions) */}
                            {activeFilter === "transactions" && (
                                <div className="flex items-center bg-gray-100 dark:bg-[#252525] p-1 rounded-xl border border-gray-200 dark:border-gray-700">
                                    {[
                                        { id: "today", label: "Today" },
                                        { id: "7d", label: "7 Days" },
                                        { id: "30d", label: "30 Days" },
                                        { id: "all_time", label: "All Time" }
                                    ].map((f) => (
                                        <button
                                            key={f.id}
                                            onClick={() => setTimeFilter(f.id)}
                                            className={`px-3 py-1.5 text-[10px] font-bold rounded-lg transition-all uppercase tracking-wider ${
                                                timeFilter === f.id 
                                                ? "bg-white dark:bg-[#327691] text-[#327691] dark:text-white shadow-sm" 
                                                : "text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                                            }`}
                                        >
                                            {f.label}
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                        
                        <div className="bg-white dark:bg-[#1f1f1f] rounded-2xl border border-gray-100 dark:border-gray-800 overflow-hidden shadow-sm">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left">
                                    <thead>
                                        <tr className="border-b border-gray-100 dark:border-gray-800">
                                            {activeFilter === "transactions" ? (
                                                <>
                                                    <th className="px-6 py-4 text-sm font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                                                    <th className="px-6 py-4 text-sm font-semibold text-gray-500 uppercase tracking-wider">Type</th>
                                                    <th className="px-6 py-4 text-sm font-semibold text-gray-500 uppercase tracking-wider">Details</th>
                                                    <th className="px-6 py-4 text-sm font-semibold text-gray-500 uppercase tracking-wider text-right">Amount</th>
                                                </>
                                            ) : (
                                                <>
                                                    <th className="px-6 py-4 text-sm font-semibold text-gray-500 uppercase tracking-wider">User</th>
                                                    <th className="px-6 py-4 text-sm font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                                                    <th className="px-6 py-4 text-sm font-semibold text-gray-500 uppercase tracking-wider">Subscription</th>
                                                    <th className="px-6 py-4 text-sm font-semibold text-gray-500 uppercase tracking-wider">Last Seen</th>
                                                </>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                                        {paginatedData.length > 0 ? paginatedData.map((item) => (
                                            <tr key={item.uid || item.id} className="hover:bg-gray-50 dark:hover:bg-white/5 transition-colors">
                                                {activeFilter === "transactions" ? (
                                                    <>
                                                        <td className="px-6 py-4 text-sm whitespace-nowrap">{new Date(item.createdAt || 0).toLocaleDateString()}</td>
                                                        <td className="px-6 py-4 text-sm whitespace-nowrap">
                                                            <span className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase ${item.type?.includes('subscription') ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'}`}>
                                                                {item.type?.replace('_', ' ') || "Purchase"}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm truncate max-w-[250px]">{item.productTitle || "Marketplace Purchase"}</td>
                                                        <td className="px-6 py-4 text-sm font-bold text-right text-emerald-500">${item.amount.toFixed(2)}</td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center space-x-3">
                                                                <img src={item.profilePic || "https://firebasestorage.googleapis.com/v0/b/zylen-app.firebasestorage.app/o/avatar-1.webp?alt=media"} className="w-10 h-10 rounded-full object-cover" alt="" />
                                                                <div>
                                                                    <p className="font-semibold text-sm">{item.name || "User"}</p>
                                                                    <p className="text-xs text-gray-500">{item.email}</p>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm">
                                                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.role === "Host" ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-700"}`}>{item.role || "Free"}</span>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm">
                                                            <span className={`capitalize ${item.subscriptionStatus === "active" ? "text-emerald-500 font-bold" : item.subscriptionStatus === "lifetime" ? "text-purple-500" : "text-gray-400"}`}>{item.subscriptionStatus || "none"}</span>
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-500">{item.lastSeen ? new Date(item.lastSeen).toLocaleString() : "Never"}</td>
                                                    </>
                                                )}
                                            </tr>
                                        )) : <tr><td colSpan="4" className="px-6 py-10 text-center text-gray-500 italic">No records found for this period.</td></tr>}
                                    </tbody>
                                </table>
                            </div>

                            {totalPages > 1 && (
                                <div className="px-6 py-4 border-t border-gray-100 dark:border-gray-800 flex items-center justify-between">
                                    <p className="text-sm text-gray-500">
                                        Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length}
                                    </p>
                                    <div className="flex items-center space-x-2">
                                        <button 
                                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                                            disabled={currentPage === 1} 
                                            className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                                        >
                                            <LuChevronLeft className="w-4 h-4" />
                                        </button>
                                        
                                        <div className="flex items-center space-x-1">
                                            {[...Array(totalPages)].map((_, i) => {
                                                const pageNum = i + 1;
                                                // Only show 5 pages at a time if there are many
                                                if (totalPages > 7) {
                                                    if (pageNum !== 1 && pageNum !== totalPages && Math.abs(pageNum - currentPage) > 1) {
                                                        if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                                                            return <span key={pageNum} className="px-1 text-gray-400">...</span>;
                                                        }
                                                        return null;
                                                    }
                                                }

                                                return (
                                                    <button
                                                        key={pageNum}
                                                        onClick={() => setCurrentPage(pageNum)}
                                                        className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                                                            currentPage === pageNum 
                                                            ? "bg-[#327691] text-white shadow-sm" 
                                                            : "text-gray-500 hover:bg-gray-100 dark:hover:bg-white/5"
                                                        }`}
                                                    >
                                                        {pageNum}
                                                    </button>
                                                );
                                            })}
                                        </div>

                                        <button 
                                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                                            disabled={currentPage === totalPages} 
                                            className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                                        >
                                            <LuChevronRight className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        </div>
    );
};

export default AdminAnalytics;
